// File: js/db/errors.js
//
// Typed refusals for the device store (spec invariant 6: "No silent failure, ever").
//
// The pre-rewrite code showed a GREEN « Transaction saved offline » for what was
// actually a unique-constraint violation (audit C1), and « Vente complétée avec
// succès ! » for a sale whose stock decrement had failed (audit C3). Every refusal
// on the write path is therefore a named, catchable error carrying a stable machine
// `code`, so the UI slice can render the exact French banner from spec §9 without
// string-matching an English message.

/** Base class, so a caller can `instanceof EngineError` to catch every refusal. */
export class EngineError extends Error {
    /**
     * @param {string} code Stable machine code. Never localised — the UI maps it.
     * @param {string} message Developer-facing detail.
     */
    constructor(code, message) {
        super(message);
        this.name = new.target.name;
        this.code = code;
    }
}

/**
 * A required field is missing or malformed on a local write. Carries EVERY problem
 * found, not just the first, so a form can surface them all at once.
 */
export class ValidationError extends EngineError {
    /** @param {{code:string, field?:string, message:string}[]} issues */
    constructor(issues) {
        const list = Array.isArray(issues) ? issues : [];
        super('validation', `invalid write: ${list.map(i => i.message).join('; ') || 'unknown'}`);
        this.issues = list;
    }
}

/**
 * This device may not accept cash right now. `reason` is exactly the string
 * `collectionAllowed()` returns, so the refusal and the status can never disagree.
 *
 * The three reasons, in the gate's own order (spec §7 — durability, identity,
 * receipt supply), each mapping to one spec §9 string:
 *
 * - `storage-not-persistent` → « Cet appareil ne peut pas conserver les paiements en
 *   sécurité — utilisez le carnet papier aujourd'hui. »
 * - `device-not-provisioned` → « Cet appareil n'est pas encore enregistré — demandez
 *   à l'administrateur de l'enregistrer. En attendant, utilisez le carnet papier. »
 * - `no-receipt-numbers` → « Plus de numéros de reçu — utilisez le carnet papier. »
 */
export class CollectionRefusedError extends EngineError {
    /** @param {import('./ledger-write.js').CollectionRefusalReason} reason */
    constructor(reason) {
        super(reason, `collection refused: ${reason}`);
        this.reason = reason;
    }
}

/**
 * Logout refused because this device still holds money the server has never seen
 * (audit C5: `performLogout()` did `await db.delete()`, destroying unsynced cash
 * with no warning and no pending check).
 */
export class LogoutRefusedError extends EngineError {
    /** @param {{pendingCount:number, pendingTotal:number}} detail */
    constructor(detail) {
        super(
            'unsynced-outbox',
            `logout refused: ${detail.pendingCount} unsynced record(s) worth ${detail.pendingTotal} FCFA`
        );
        this.detail = detail;
    }
}
